<template>
  <div class="schema-view-page">
    <div class="schema-view-controls">
      <label for="schema-view-app-name">App Name</label>
      <div class="schema-view-controls-row">
        <input
          id="schema-view-app-name"
          v-model="appName"
          type="text"
          placeholder="Enter app name"
        >
        <button
          type="button"
          class="schema-view-load-btn"
          :disabled="isLoading"
          @click="loadSchema"
        >
          {{ isLoading ? 'Loading...' : 'Reload Schema' }}
        </button>
        <div v-if="errorMessage" class="schema-view-error-inline">
          {{ errorMessage }}
        </div>
      </div>
    </div>
    <hr class="schema-view-separator">

    <div v-if="isLoading" class="schema-view-loading">
      Loading schema...
    </div>

    <div v-else-if="schemaFields.length" class="schema-view-layout">
      <div class="schema-view-card">
        <SchemaGroup
          :key="schemaRenderKey"
          v-model:params="schemaParams"
          :fields="schemaFields"
        />
      </div>

      <div class="schema-view-output-panel">
        <div class="schema-view-output">
          <div class="schema-view-output-actions">
            <button
              type="button"
              class="schema-view-load-btn"
              @click="generateOutput"
            >
              Generate Output
            </button>
            <button
              type="button"
              class="schema-view-load-btn"
              :disabled="!formattedOutput"
              @click="copyOutput"
            >
              {{ copyButtonLabel }}
            </button>
          </div>

          <pre v-if="formattedOutput" class="schema-view-output-json" v-html="formattedOutputHtml" />
        </div>
      </div>
    </div>

    <div v-else-if="!errorMessage" class="schema-view-empty">
      No schema returned for this app.
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';

const route = useRoute();
const apiBaseUrl = 'http://localhost:8081';

const appName = ref('');
const isLoading = ref(false);
const errorMessage = ref('');
const schemaFields = ref([]);
const schemaParams = ref({});
const schemaRenderKey = ref(0);
const formattedOutput = ref('');
const formattedOutputHtml = ref('');
const copyButtonLabel = ref('Copy JSON');

async function rpcFetch(method, params = {}) {
  const response = await fetch(`${apiBaseUrl}/api?method=${encodeURIComponent(method)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    }),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
}

async function loadSchema() {
  isLoading.value = true;
  errorMessage.value = '';
  schemaFields.value = [];
  schemaParams.value = {};
  formattedOutput.value = '';
  formattedOutputHtml.value = '';
  copyButtonLabel.value = 'Copy JSON';

  try {
    const payloadName = String(appName.value || '').trim();
    const response = await rpcFetch('app.get', { name: payloadName });

    if (response?.error) {
      if (String(response.error.message || '').toLowerCase() === 'name is required.') {
        return;
      }
      errorMessage.value = response.error.message || 'app.get returned an error.';
      return;
    }

    const returnedSchema = response?.result?.schema;
    if (!Array.isArray(returnedSchema)) {
      errorMessage.value = 'No valid schema array found in app.get response.';
      return;
    }

    schemaFields.value = returnedSchema;
    schemaRenderKey.value += 1;
  } catch (error) {
    errorMessage.value = error?.message || String(error);
  } finally {
    isLoading.value = false;
  }
}

function generateOutput() {
  formattedOutput.value = JSON.stringify(schemaParams.value, null, 2);
  formattedOutputHtml.value = highlightJson(formattedOutput.value);
  copyButtonLabel.value = 'Copy JSON';
}

async function copyOutput() {
  if (!formattedOutput.value) {
    return;
  }

  await navigator.clipboard.writeText(formattedOutput.value);
  copyButtonLabel.value = 'Copied';
}

function highlightJson(jsonString) {
  const escaped = jsonString
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"\s*:?)|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?)/g,
    (match, stringToken, keywordToken, numberToken) => {
      if (stringToken) {
        if (stringToken.endsWith(':')) {
          return `<span class="json-key">${stringToken}</span>`;
        }
        return `<span class="json-string">${stringToken}</span>`;
      }

      if (keywordToken) {
        return keywordToken === 'null'
          ? `<span class="json-null">${keywordToken}</span>`
          : `<span class="json-boolean">${keywordToken}</span>`;
      }

      if (numberToken) {
        return `<span class="json-number">${numberToken}</span>`;
      }

      return match;
    },
  );
}

onMounted(() => {
  const queryName = typeof route.query.name === 'string' ? route.query.name : '';
  appName.value = queryName;
  loadSchema();
});
</script>

<style lang="scss" scoped>
.schema-view-page {
  margin: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.schema-view-header h1 {
  margin: 0;
}

.schema-view-header p {
  margin: 6px 0 0;
  color: #666;
}

.schema-view-controls {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.schema-view-controls-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.schema-view-separator {
  width: 100%;
  border: 0;
  border-top: 1px solid #ddd;
  margin: 0;
}

.schema-view-controls label {
  font-weight: 600;
  font-size: 12px;
}

.schema-view-controls input {
  min-width: 260px;
  height: 32px;
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
}

.schema-view-load-btn {
  height: 32px;
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: #2b6cb0;
  color: #fff;
  font-weight: 600;
}

.schema-view-load-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.schema-view-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.schema-view-card {
  flex: 1 1 600px;
  max-width: 600px;
  min-width: 320px;
  max-height: calc(100vh - 220px);
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  overflow-y: auto;
  overflow-x: hidden;
}

.schema-view-error-inline {
  color: #b22323;
  font-size: 12px;
}

.schema-view-output {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schema-view-output-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.schema-view-output-panel {
  flex: 0 1 600px;
  min-width: 420px;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
}

.schema-view-output-json {
  margin: 0;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  min-height: 500px;
  max-height: calc(100vh - 260px);
  background: #2f343b;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 16px;
  line-height: 1.5;
  color: #e7edf3;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  overflow-x: auto;
}

.schema-view-output-json :deep(.json-key) {
  color: #7dd3fc;
}

.schema-view-output-json :deep(.json-string) {
  color: #86efac;
}

.schema-view-output-json :deep(.json-number) {
  color: #f9a8d4;
}

.schema-view-output-json :deep(.json-boolean) {
  color: #fcd34d;
}

.schema-view-output-json :deep(.json-null) {
  color: #c4b5fd;
}

.schema-view-loading,
.schema-view-empty {
  color: #555;
}
</style>